import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import authProfileState from "../../states/authProfileState";
import { useRecoilValue } from "recoil";
import getSchema from "../../lib/api/getSchema";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import MomSysUglOrderPurchaseDetail from "./MomSysUglOrderPurchaseDetail";
import layoutConf from "../../config/layoutConf";
import { CellType } from "@grapecity/wijmo.grid";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import UpdateRecordButton from "../input/UpdateRecordButton";
import DeleteRecordButton from "../input/DeleteRecordButton";
import ApprovalButton from "../input/ApprovalButton";
import SaveToExcelButton from "../input/SaveToExcelButton";
import notificationState from "../../states/notificationState";
import { useSetRecoilState } from "recoil";
import getSpecific from "../../lib/api/getSpecific";
import CreateRecordButton from "../input/CreateRecordButton";
import { cloneDeep, debounce } from "lodash";
import defineConf from "../../config/defineConf";
import MomSysUglOrderPurchasMstPur2ExcelFormDownloadButton from "./../input/MomSysUglOrderPurchasMstPur2ExcelFormDownloadButton";
import MomSysUglOrderPurchasMstMaterialExcelFormDownloadButton from "./../input/MomSysUglOrderPurchasMstMaterialExcelFormDownloadButton";

function MomSysUglOrderPurchaseMst(props) {
    const id = "MomSysUglOrderPurchaseMst";
    const table = "MomSysUglOrderPurchaseMst";
    const view = "MomSysZvwOrderPurchaseMst";

    const [height, setHeight] = useState(0);

    const { t } = useTranslation();

    const authProfile = useRecoilValue(authProfileState);
    const [approvalCtx, setApprovalCtx] = useState();

    const [orderPurchaseMstSchema, setOrderPurchaseMstSchema] = useState();
    const [orderPurchaseMstUpdateSchema, setOrderPurchaseMstUpdateSchema] = useState();

    const [orderPurchaseMstGrid, setOrderPurchaseMstGrid] = useState();
    const [orderPurchaseMstApi, setOrderPurchaseMstApi] = useState();
    const [orderPurchaseMstData, setOrderPurchaseMstData] = useState();
    const [orderPurchaseMstDataEmpty, setOrderPurchaseMstDataEmpty] = useState();
    const [orderPurchaseMstSelect, setOrderPurchaseMstSelect] = useState();

    const [orderPurchaseDetailDataEmpty, setOrderPurchaseDetailDataEmpty] = useState();

    const setNotification = useSetRecoilState(notificationState);

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    useEffect(() => {
        let mounted = true;

        window.addEventListener("resize", handleResize);
        handleResize();

        (async () => {
            let schema = await getSchema(table);

            if (mounted) {
                setOrderPurchaseMstSchema(schema);
            }
        })();

        return () => {
            mounted = false;
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        let mounted = true;

        (async () => {
            if (orderPurchaseMstSchema) {
                const modifiedSchema = cloneDeep(orderPurchaseMstSchema);

                modifiedSchema.columns.forEach((column) => {
                    switch (column.name) {
                        case "companyCode":
                            column.readonly = true;

                            break;

                        default:
                            break;
                    }
                });

                if (mounted) {
                    setOrderPurchaseMstUpdateSchema(modifiedSchema);
                }
            }
        })();

        return () => {
            mounted = false;
        };
    }, [orderPurchaseMstSchema]);

    useEffect(() => {
        let approvalCtx = {
            approvalTypeId: 8,
            tableName: table,
            statusColumn: "approved",
            schema: orderPurchaseMstSchema,
            onValidateApprovalItem: (item) => {
                if (item.createdBy !== authProfile.username) {
                    return [false, t("error.approvalRequesterNotMatch")];
                }

                return [true, ""];
            },
            makeRequestItemDescription: (item) => {
                return `${t("approvalRequestMsg.msg.po", {
                    poName: item.orderPurchaseName,
                    poNumber: item.orderPurchaseId,
                })}`; // Use Translation on Production Code
            },
        };

        setApprovalCtx(approvalCtx);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderPurchaseMstSchema]);

    useEffect(() => {
        (async () => {
            if (orderPurchaseMstGrid) {
                orderPurchaseMstGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setOrderPurchaseMstSelect(grid.selectedItems[0]);
                        }
                    }),
                    10
                );

                orderPurchaseMstGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setOrderPurchaseMstSelect(grid.selectedItems[0]);
                        }
                    }),
                    10
                );
            }
        })();
    }, [orderPurchaseMstGrid]);

    const exportFormatItem = (args) => {
        let { panel, col, row, xlsxCell } = args;

        if (panel.cellType === CellType.Cell) {
            switch (panel.columns[col].binding) {
                case "approvalState":
                    switch (orderPurchaseMstData[row][panel.columns[col].binding]) {
                        case defineConf.approvalState.PENDING:
                            xlsxCell.value = t("selectItems.approvalState.approvalPending");

                            break;

                        case defineConf.approvalState.PARTIALLY_APPROVED:
                            xlsxCell.value = t("selectItems.approvalState.partiallyApproved");

                            break;

                        case defineConf.approvalState.APPROVED:
                            xlsxCell.value = t("selectItems.approvalState.totallyApproved");

                            break;

                        case defineConf.approvalState.REJECTED:
                            xlsxCell.value = t("selectItems.approvalState.approvalRejected");

                            break;

                        case defineConf.approvalState.WITHDRAWN:
                            xlsxCell.value = t("selectItems.approvalState.approvalWithdrawn");

                            break;

                        case defineConf.approvalState.CANCELLED:
                            xlsxCell.value = t("selectItems.approvalState.approvalCancelled");

                            break;

                        default:
                            break;
                    }

                    break;

                default:
                    break;
            }
        }
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
    };

    const onFailure = (reason) => {
        notifyFailure(reason.response ? reason.response.data : reason);
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
    };

    const onUpdateMaster = () => {
        if (orderPurchaseMstGrid) {
            (async () => {
                const purchaseMst = await getSpecific(
                    "MomSysZvwOrderPurchaseMst",
                    orderPurchaseMstGrid.selectedItems[0].orderPurchaseId
                );

                orderPurchaseMstGrid.selectedItems[0].cost = purchaseMst.cost;
                orderPurchaseMstGrid.selectedItems[0].inputState = purchaseMst.inputState;
                orderPurchaseMstGrid.selectedItems[0].balancePayment = purchaseMst.balancePayment;

                orderPurchaseMstGrid.refresh(false); // refresh grid content only
            })();
        }
    };

    return (
        <div style={{ height: height }}>
            <ReflexContainer orientation="horizontal">
                <ReflexElement flex={0.6}>
                    <BasicCrudScreenType
                        id={id}
                        headerTitle={t("term.po")}
                        table={table}
                        view={view}
                        onInitialized={(grid) => {
                            setOrderPurchaseMstGrid(grid);
                        }}
                        onApiInitialized={(api) => {
                            setOrderPurchaseMstApi(api);
                        }}
                        onLoadData={(data) => {
                            setOrderPurchaseMstData(data);
                            setOrderPurchaseMstDataEmpty(data.length < 1 ? true : false);
                        }}
                        exportFormatItem={exportFormatItem}
                        noCreate
                        noEdit
                        noDelete
                        noExcel
                        onAppendButtonInFrontOfSearch={() => (
                            <>
                                <BasicCrudScreenActionItemLayout>
                                    <AccessControl
                                        accessTarget={orderPurchaseMstSchema && orderPurchaseMstSchema.name}
                                        accessMethod="POST"
                                    >
                                        <CreateRecordButton
                                            id={id}
                                            schema={orderPurchaseMstSchema}
                                            refresh={orderPurchaseMstApi}
                                            onSuccess={onSuccess}
                                        />
                                    </AccessControl>

                                    <AccessControl
                                        accessTarget={orderPurchaseMstSchema && orderPurchaseMstSchema.name}
                                        accessMethod={
                                            orderPurchaseMstSchema &&
                                            (orderPurchaseMstSchema.updateWithPatch ? "PATCH" : "PUT")
                                        }
                                    >
                                        <UpdateRecordButton
                                            id={id}
                                            schema={orderPurchaseMstUpdateSchema}
                                            grid={orderPurchaseMstGrid}
                                            refresh={orderPurchaseMstApi}
                                            disabled={
                                                orderPurchaseMstSelect?.approvalState || orderPurchaseMstDataEmpty
                                            }
                                            //disabled={orderPurchaseMstDataEmpty}
                                            onSuccess={onSuccess}
                                        />
                                    </AccessControl>

                                    <AccessControl
                                        accessTarget={orderPurchaseMstSchema && orderPurchaseMstSchema.name}
                                        accessMethod="DELETE"
                                    >
                                        <DeleteRecordButton
                                            id={id}
                                            schema={orderPurchaseMstSchema}
                                            grid={orderPurchaseMstGrid}
                                            refresh={orderPurchaseMstApi}
                                            color="secondary"
                                            disabled={
                                                orderPurchaseMstSelect?.approvalState || orderPurchaseMstDataEmpty
                                            }
                                            onSuccess={onSuccess}
                                            onFailure={onFailure}
                                            onPartialFailure={onNotifyWarning}
                                        />
                                    </AccessControl>

                                    <AccessControl
                                        accessTarget={orderPurchaseMstSchema && orderPurchaseMstSchema.name}
                                        accessMethod="APPROVAL"
                                    >
                                        <ApprovalButton
                                            id={id}
                                            grid={orderPurchaseMstGrid}
                                            refresh={orderPurchaseMstApi}
                                            approvalCtx={approvalCtx}
                                            disabled={orderPurchaseMstDataEmpty || orderPurchaseDetailDataEmpty}
                                            onSuccess={onSuccess}
                                            onFailure={onFailure}
                                        />
                                    </AccessControl>

                                    <AccessControl
                                        accessTarget={orderPurchaseMstSchema && orderPurchaseMstSchema.name}
                                        accessMethod="SAVETOEXCEL"
                                    >
                                        <SaveToExcelButton
                                            id={id}
                                            grid={orderPurchaseMstGrid}
                                            view={view}
                                            color="primary"
                                            exportFormatItem={exportFormatItem}
                                            disabled={orderPurchaseMstDataEmpty}
                                        />
                                    </AccessControl>
                                    <AccessControl
                                        accessTarget={orderPurchaseMstSchema && orderPurchaseMstSchema.name}
                                        accessMethod="GET"
                                    >
                                        <MomSysUglOrderPurchasMstMaterialExcelFormDownloadButton
                                            id={id}
                                            schema={orderPurchaseMstSchema}
                                            refresh={orderPurchaseMstApi}
                                            grid={orderPurchaseMstGrid}
                                            orderPurchaseMstSelect={orderPurchaseMstSelect}
                                            onSuccess={onSuccess}
                                        />
                                    </AccessControl>
                                    <AccessControl
                                        accessTarget={orderPurchaseMstSchema && orderPurchaseMstSchema.name}
                                        accessMethod="GET"
                                    >
                                        <MomSysUglOrderPurchasMstPur2ExcelFormDownloadButton
                                            id={id}
                                            schema={orderPurchaseMstSchema}
                                            refresh={orderPurchaseMstApi}
                                            grid={orderPurchaseMstGrid}
                                            orderPurchaseMstSelect={orderPurchaseMstSelect}
                                            onSuccess={onSuccess}
                                        />
                                    </AccessControl>
                                </BasicCrudScreenActionItemLayout>
                            </>
                        )}
                    />
                </ReflexElement>

                <ReflexSplitter style={{ height: layoutConf.reflex.splitterHeight }} />

                <ReflexElement>
                    {orderPurchaseMstSelect ? (
                        <MomSysUglOrderPurchaseDetail
                            orderPurchaseMstSelect={orderPurchaseMstSelect}
                            onUpdateMaster={onUpdateMaster}
                            onDetailEmpty={(isDetailEmpty) => {
                                setOrderPurchaseDetailDataEmpty(isDetailEmpty);
                            }}
                        />
                    ) : (
                        <></>
                    )}
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
}

export default MomSysUglOrderPurchaseMst;
